import React, { useState, useEffect } from 'react';
import PureHtmlEditor from './PureHtmlEditor';
import './PureHtmlEditorModal.css';

const PureHtmlEditorModal = ({ isOpen, onClose, initialContent, onSave }) => {
  const [htmlContent, setHtmlContent] = useState('');

  // 리사이즈 핸들을 숨기는 함수
  const hideResizeHandles = () => {
    // 모든 가능한 리사이즈 핸들 선택자들
    const selectors = [
      '.react-resizable-handle',
      '[class*="resizable-handle"]',
      '[class*="Resizer"]', 
      '[class*="resizer"]',
      '[data-yoopta-block-type="Image"] [style*="cursor"]',
      '[data-yoopta-editor] [style*="cursor"][style*="resize"]',
      '[style*="position: absolute"][style*="cursor"][style*="resize"]',
      '[style*="position: absolute"][style*="width"][style*="height"][style*="cursor"]'
    ];
    
    // 각 선택자로 요소를 찾아 숨김
    selectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.pointerEvents = 'none';
          element.style.opacity = '0';
          element.style.zIndex = '-1';
        });
      } catch (e) {
        console.warn('Failed to hide elements with selector:', selector);
      }
    });

    // 인라인 스타일로 cursor가 resize 관련인 모든 요소 숨김
    const allElements = document.querySelectorAll('*');
    allElements.forEach(element => {
      const style = window.getComputedStyle(element);
      const cursor = style.cursor;
      if (cursor && (
        cursor.includes('resize') || 
        cursor.includes('col-resize') ||
        cursor.includes('row-resize') ||
        cursor.includes('nw-resize') ||
        cursor.includes('ne-resize') ||
        cursor.includes('sw-resize') ||
        cursor.includes('se-resize')
      )) {
        // yoopta 관련 요소인지 확인
        const isYooptaRelated = element.closest('[data-yoopta-editor]') || 
                               element.closest('[class*="yoo-image"]') ||
                               element.closest('[data-yoopta-block-type="Image"]');
        
        if (isYooptaRelated) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.pointerEvents = 'none';
          element.style.opacity = '0';
        }
      }
    });
  };

  // 리사이즈 핸들을 다시 보이게 하는 함수
  const showResizeHandles = () => {
    // 1. 클래스 기반 리사이즈 핸들 복원
    const resizeSelectors = [
      '.react-resizable-handle',
      '[class*="resizable-handle"]',
      '[class*="Resizer"]', 
      '[class*="resizer"]'
    ];
    
    resizeSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.display = '';
          element.style.visibility = '';
          element.style.pointerEvents = '';
          element.style.opacity = '';
          element.style.zIndex = '';
        });
      } catch (e) {
        console.warn('Failed to show elements with selector:', selector);
      }
    });

    // 2. yoopta 관련 숨겨진 요소들 복원
    const yooptaElements = document.querySelectorAll('[data-yoopta-editor] [style*="display: none"]');
    yooptaElements.forEach(element => {
      const cursor = element.style.cursor || window.getComputedStyle(element).cursor;
      if (cursor && cursor.includes('resize')) {
        element.style.display = '';
        element.style.visibility = '';
        element.style.pointerEvents = '';
        element.style.opacity = '';
        element.style.zIndex = '';
      }
    });
  };

  useEffect(() => {
    if (isOpen) {
      console.log('[PureHtmlEditorModal] 초기 콘텐츠 설정:', {
        length: initialContent?.length,
        hasStyle: initialContent?.includes('<style>')
      });
      setHtmlContent(initialContent || '');
      
      // body에 클래스 추가
      document.body.classList.add('pure-html-modal-open');
      
      // 리사이즈 핸들 숨김 (약간의 지연을 두어 DOM이 준비된 후 실행)
      setTimeout(hideResizeHandles, 100);
      
      // MutationObserver로 동적으로 추가되는 리사이즈 핸들도 감지
      const observer = new MutationObserver(() => {
        hideResizeHandles();
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      
      // cleanup 함수에서 observer 해제
      return () => {
        observer.disconnect();
      };
    } else {
      // 모달이 닫힐 때 정리
      document.body.classList.remove('pure-html-modal-open');
      showResizeHandles();
    }
  }, [isOpen]); // initialContent를 의존성에서 제거하여 무한 루프 방지

  const handleSave = () => {
    // Pure HTML 저장 시 CSS가 포함된 완전한 HTML 전달
    // RichEditorControl에서 CSS를 분리하여 저장함
    console.log('[PureHtmlEditorModal] 저장할 HTML:', {
      length: htmlContent?.length,
      hasStyle: htmlContent?.includes('<style>'),
      preview: htmlContent?.substring(0, 200)
    });
    onSave(htmlContent, null);
    onClose();
  };

  const handleCancel = () => {
    setHtmlContent(initialContent || '');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="pure-html-editor-modal-overlay">
      <div className="pure-html-editor-modal">
        <div className="pure-html-editor-modal-header">
          <h2>Pure HTML 편집</h2>
          <button className="close-button" onClick={handleCancel}>×</button>
        </div>
        
        <div className="pure-html-editor-modal-body">
          <PureHtmlEditor
            initialContent={htmlContent}
            onChange={setHtmlContent}
            placeholder="HTML 콘텐츠를 입력하세요..."
          />
        </div>
        
        <div className="pure-html-editor-modal-footer">
          <button className="cancel-button" onClick={handleCancel}>취소</button>
          <button className="save-button" onClick={handleSave}>저장</button>
        </div>
      </div>
    </div>
  );
};

export default PureHtmlEditorModal;